<template>
  <div>
    <LoadingBlock :loading="loading">
      <div v-if="!certificates.length" class="text-muted mb-3">
        Сертификаты не привязаны
      </div>

      <table v-else class="table mb-3">
        <thead>
        <tr>
          <th>Номер</th>
          <th>Вид</th>
          <th>Срок действия</th>
          <th>Статус</th>
          <th></th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="cert in certificates" :key="cert.id">
          <td>
            <router-link :to="{ name: 'certificate-detail', params: { id: cert.id } }">
              {{ cert.number }}
            </router-link>
          </td>
          <td>{{ getKindLabel(cert.kind) }}</td>
          <td>{{ cert.is_perpetual ? 'Бессрочный' : formatCertificateDate(cert.valid_until) }}</td>
          <td>
            <span v-if="isCertificateActive(cert)" class="badge bg-label-success">Актуален</span>
            <span v-else class="badge bg-label-secondary">Истёк</span>
          </td>
          <td class="text-end">
            <button type="button" class="btn btn-icon btn-label-secondary waves-effect"
                    @click="unlink(cert.id)">
              <span class="ti ti-trash ti-md"></span>
            </button>
          </td>
        </tr>
        </tbody>
      </table>

      <div class="border-top pt-3">
        <label class="form-label">Привязать сертификат</label>
        <div class="row gy-2 mb-3">
          <div class="col-md-4">
            <input type="text" class="form-control" placeholder="Номер"
                   v-model="searchData.number" @change="searchCertificates">
          </div>
          <div class="col-md-4">
            <input type="text" class="form-control" placeholder="Бренд"
                   v-model="searchData.brand" @change="searchCertificates">
          </div>
          <div class="col-md-4">
            <input type="text" class="form-control" placeholder="Артикул"
                   v-model="searchData.article" @change="searchCertificates">
          </div>
          <div class="col-md-4">
            <input type="text" class="form-control" placeholder="ТНВЭД"
                   v-model="searchData.tnved" @change="searchCertificates">
          </div>
          <div class="col-md-4">
            <Select v-model="searchData.kind" :options="kindOptions" optionLabel="name" optionValue="code"
                    placeholder="Вид" showClear style="width: 100%" @change="searchCertificates"/>
          </div>
          <div class="col-md-4">
            <Select v-model="searchData.compliance_type" :options="complianceOptions" optionLabel="name"
                    optionValue="code" placeholder="Тип соответствия" showClear style="width: 100%"
                    @change="searchCertificates"/>
          </div>
          <div class="col-md-12">
            <Select v-model="selectedCertificateId" :options="certificateOptions" optionLabel="label"
                    optionValue="id" placeholder="Выберите сертификат" filter style="width: 100%"/>
          </div>
        </div>
        <button type="button" class="btn btn-primary mt-3" :disabled="!selectedCertificateId"
                @click="linkSelected">
          Привязать
        </button>
      </div>

      <div class="border-top pt-3 mt-4">
        <a href="#" @click.prevent="showLinksForm = !showLinksForm">
          {{ showLinksForm ? 'Скрыть загрузку по ссылкам' : 'Загрузить сертификаты по ссылкам и привязать к товару' }}
        </a>
        <div v-if="showLinksForm" class="mt-3">
          <CertificateFromLinksForm :key="productId" :product-ids="[productId]" :navigate-to-task="false"/>
        </div>
      </div>
    </LoadingBlock>
  </div>
</template>

<script>
import LoadingBlock from "@/components/page/LoadingBlock.vue";
import CertificateFromLinksForm from "@/components/forms/CertificateFromLinks.vue";
import {Select} from "primevue";
import {getByProduct, getList, linkProducts, unlinkProducts} from "@/api/certificate";
import {user} from "@/data/user";
import {Message, Messages} from "@/data/messages";
import {
    certificateKinds,
    complianceTypes,
    formatCertificateDate,
    getKindLabel,
    isCertificateActive,
    parseCertificateError
} from "@/data/certificate";

export default {
  name: "ProductCertificates",
  components: {LoadingBlock, Select, CertificateFromLinksForm},
  props: {
    productId: {
      type: String,
      required: true,
    },
    productBrand: {
      type: String,
      default: '',
    },
  },
  emits: ['changed'],
  data() {
    return {
      loading: false,
      certificates: [],
      certificateOptions: [],
      selectedCertificateId: null,
      showLinksForm: false,
      kindOptions: certificateKinds,
      complianceOptions: complianceTypes,
      searchData: {
        number: '',
        brand: '',
        article: '',
        tnved: '',
        kind: null,
        compliance_type: null,
      },
    }
  },
  methods: {
    getKindLabel,
    formatCertificateDate,
    isCertificateActive,
    emitSummary() {
      const activeCertificates = this.certificates.filter((cert) => isCertificateActive(cert))
      this.$emit('changed', {
        active_certificates_count: activeCertificates.length,
        active_certificate_numbers: activeCertificates.map((cert) => cert.number),
      })
    },
    async load() {
      this.loading = true
      try {
        const result = await getByProduct(this.productId)
        this.certificates = result.data || []
        this.emitSummary()
      } finally {
        this.loading = false
      }
    },
    async searchCertificates() {
      const query = {size: 50}
      Object.entries(this.searchData).forEach(([key, value]) => {
        if (value) {
          query[key] = value
        }
      })
      const result = await getList(user.active_company, query)
      const linkedIds = new Set(this.certificates.map((cert) => cert.id))
      this.certificateOptions = (result.data.items || [])
          .filter((cert) => !linkedIds.has(cert.id))
          .map((cert) => ({
            id: cert.id,
            label: `${cert.number} — ${getKindLabel(cert.kind)}`,
          }))
    },
    async linkSelected() {
      if (!this.selectedCertificateId) {
        return
      }
      this.loading = true
      const message = new Message()
      try {
        await linkProducts(this.selectedCertificateId, [this.productId])
        message.type = 'success'
        message.autoremove = 10
        message.text = 'Сертификат привязан'
        Messages.list.push(message)
        this.selectedCertificateId = null
        await this.load()
        await this.searchCertificates()
      } catch (e) {
        message.type = 'failure'
        message.text = parseCertificateError(e)
        Messages.list.push(message)
      }
      this.loading = false
    },
    async unlink(certificateId) {
      if (!confirm('Отвязать сертификат от товара?')) {
        return
      }
      this.loading = true
      const message = new Message()
      try {
        await unlinkProducts(certificateId, [this.productId])
        message.type = 'success'
        message.autoremove = 10
        message.text = 'Сертификат отвязан'
        Messages.list.push(message)
        await this.load()
        await this.searchCertificates()
      } catch (e) {
        message.type = 'failure'
        message.text = parseCertificateError(e)
        Messages.list.push(message)
      }
      this.loading = false
    },
  },
  async mounted() {
    this.searchData.brand = this.productBrand || ''
    await this.load()
    await this.searchCertificates()
  },
  watch: {
    productId() {
      this.load()
      this.searchCertificates()
    },
    productBrand(newValue) {
      this.searchData.brand = newValue || ''
      this.searchCertificates()
    },
  },
}
</script>
